// frontend/src/components/utilities/STLPreviewBox.js
import React from 'react';

/**
 * STLPreviewBox
 * - Renders the 81vmin preview box in STLGenerator:
 *   - The sliding "Extrusion Settings" overlay
 *   - The fallback 2D image layers (processed + overlay)
 *   - The injected Viewer (children) when hasGenerated is true
 *
 * Parent-owned state:
 * - showExtrusionOverlayPanel / setShowExtrusionOverlayPanel
 * - extrusionValues + handlers
 * - makeItFlat + setMakeItFlat
 *
 * Only inherited behavior (per your note):
 * - whether "Make it Flat" is enabled/disabled (makeItFlatDisabled prop)
 */
const STLPreviewBox = React.forwardRef(function STLPreviewBox(
  {
    // Rendering mode
    hasGenerated = false,

    // Overlay (extrusion settings)
    showExtrusionOverlayPanel,
    setShowExtrusionOverlayPanel,

    // Colors + extrusions
    colorsForUi = [],
    extrusionValues = [],
    controlsLocked = false,
    stepExtrusion,
    handleExtrusionChange,
    handleExtrusionBlur,

    // Make-it-flat (state still lives in STLGenerator)
    makeItFlat,
    setMakeItFlat,

    // This is the ONLY thing inherited from parent (enabled/disabled state)
    makeItFlatDisabled = false,

    // Fallback images when viewer isn't shown
    processedImage,
    overlayImage,

    // Children = viewer node
    children,
  },
  ref
) {
  const canEdit = !controlsLocked;

  // Final disabled state for Make it Flat checkbox:
  // - locked by STLGenerator flow OR disabled by parent rule
  const flatDisabled = controlsLocked || makeItFlatDisabled;

  return (
    <div
      ref={ref}
      style={{
        marginTop: '1.2vh',
        width: '79vmin',
        height: '79vmin',
        border: '2px dashed #94a3b8',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Sliding Extrusion Controls overlay */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          zIndex: 30,
          background:
            'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
          color: 'white',
          transform: showExtrusionOverlayPanel
            ? 'translateY(0%)'
            : 'translateY(calc(-100% + 2.8vh))',
          transition: 'transform 0.25s ease-in-out',
          willChange: 'transform',
          fontSize: '0.8rem',
          pointerEvents: 'auto',
        }}
      >
        {/* Handle */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            setShowExtrusionOverlayPanel?.((prev) => !prev);
          }}
          style={{
            height: '2.8vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderBottom: '1px solid rgba(255,255,255,0.4)',
            backgroundColor: 'rgba(112,112,112,0.4)',
          }}
        >
          <span
            style={{
              fontSize: '2.5vh',
              fontWeight: 600,
              letterSpacing: '0.03em',
            }}
          >
            {showExtrusionOverlayPanel
              ? '▲ Extrusion Settings ▲'
              : '▼ Extrusion Settings ▼'}
          </span>
        </div>

        {/* CONTENT */}
        <div
          style={{
            padding: showExtrusionOverlayPanel ? '4px 8px 6px' : '0 8px 0',
            maxHeight: showExtrusionOverlayPanel ? '65%' : 0,
            overflowY: 'auto',
            backgroundColor: 'rgba(112,112,112,0.5)',
            fontSize: '1.8vh',
            opacity: showExtrusionOverlayPanel ? 1 : 0,
            transition:
              'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
          }}
        >
          {Array.isArray(colorsForUi) &&
            colorsForUi.length > 0 &&
            Array.isArray(extrusionValues) && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.2vh',
                  width: '100%',
                }}
              >
                {/* Headers */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingBottom: '2px',
                  }}
                >
                  <div
                    style={{
                      flexBasis: '50%',
                      maxWidth: '50%',
                      paddingRight: '0.4vw',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontWeight: 'bold', fontSize: '1.6vh' }}>
                      Colors
                    </span>
                  </div>

                  <div
                    style={{
                      flexBasis: '50%',
                      maxWidth: '50%',
                      paddingLeft: '0.4vw',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5vh',
                        marginLeft: '0.6vw',
                      }}
                    >
                      <div style={{ width: '2.6vh', height: '2.6vh' }} />
                      <div
                        style={{
                          width: '4.6vw',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 'bold',
                            fontSize: '1.6vh',
                            textAlign: 'center',
                            width: '100%',
                          }}
                        >
                          Layer Height (mm)
                        </span>
                      </div>
                      <div style={{ width: '2.6vh', height: '2.6vh' }} />
                    </div>
                  </div>
                </div>

                {/* Background row (disabled controls) */}
                <div
                  key="background-overlay"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '0.1vh',
                  }}
                >
                  <div
                    style={{
                      flexBasis: '50%',
                      maxWidth: '50%',
                      paddingRight: '0.5vw',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: '1.6vh' }}>
                      {colorsForUi?.[0] || 'Background'}
                    </span>
                  </div>

                  <div
                    style={{
                      flexBasis: '50%',
                      maxWidth: '50%',
                      paddingLeft: '0.5vw',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: '0.5vh',
                    }}
                  >
                    <button
                      type="button"
                      disabled
                      style={{
                        width: '2.6vh',
                        height: '2.6vh',
                        borderRadius: '50%',
                        border: '1px solid rgba(148,163,184,0.9)',
                        backgroundColor: 'rgba(148,163,184,0.45)',
                        color: '#e5e7eb',
                        fontSize: '1.8vh',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'not-allowed',
                        padding: 0,
                      }}
                    >
                      −
                    </button>

                    <input
                      type="text"
                      value={extrusionValues[0] ?? 1}
                      disabled
                      style={{
                        width: '4.6vw',
                        fontSize: '1.4vh',
                        padding: '0.4vh',
                        textAlign: 'center',
                        backgroundColor: '#e5e7eb',
                        color: '#6b7280',
                        border: '1px solid rgba(148,163,184,0.9)',
                        borderRadius: '0.9vh',
                        cursor: 'not-allowed',
                      }}
                    />

                    <button
                      type="button"
                      disabled
                      style={{
                        width: '2.6vh',
                        height: '2.6vh',
                        borderRadius: '50%',
                        border: '1px solid rgba(148,163,184,0.9)',
                        backgroundColor: 'rgba(148,163,184,0.45)',
                        color: '#e5e7eb',
                        fontSize: '1.8vh',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'not-allowed',
                        padding: 0,
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Remaining colors */}
                {colorsForUi.slice(1).map((color, i) => (
                  <div
                    key={`overlay-color-${i + 1}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '0.1vh',
                    }}
                  >
                    <div
                      style={{
                        flexBasis: '50%',
                        maxWidth: '50%',
                        paddingRight: '0.5vw',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: '1.6vh' }}>{color}</span>
                    </div>

                    <div
                      style={{
                        flexBasis: '50%',
                        maxWidth: '50%',
                        paddingLeft: '0.5vw',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '0.5vh',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => canEdit && stepExtrusion?.(i + 1, -1)}
                        disabled={!canEdit}
                        style={{
                          width: '2.6vh',
                          height: '2.6vh',
                          borderRadius: '50%',
                          border: '1px solid rgba(148,163,184,0.9)',
                          backgroundColor: !canEdit
                            ? 'rgba(148,163,184,0.45)'
                            : '#0f172a',
                          color: '#e5e7eb',
                          fontSize: '1.8vh',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: !canEdit ? 'not-allowed' : 'pointer',
                          padding: 0,
                        }}
                      >
                        −
                      </button>

                      <input
                        type="text"
                        inputMode="decimal"
                        value={extrusionValues[i + 1] ?? 1}
                        onChange={(e) =>
                          handleExtrusionChange?.(i + 1, e.target.value)
                        }
                        onBlur={() => handleExtrusionBlur?.(i + 1)}
                        disabled={!canEdit}
                        style={{
                          width: '4.6vw',
                          fontSize: '1.4vh',
                          padding: '0.4vh',
                          textAlign: 'center',
                          backgroundColor: !canEdit
                            ? '#e5e7eb'
                            : 'rgba(15,23,42,0.9)',
                          color: '#e5e7eb',
                          border: '1px solid rgba(148,163,184,0.9)',
                          borderRadius: '0.9vh',
                          cursor: !canEdit ? 'not-allowed' : 'text',
                          outline: 'none',
                        }}
                      />

                      <button
                        type="button"
                        onClick={() => canEdit && stepExtrusion?.(i + 1, +1)}
                        disabled={!canEdit}
                        style={{
                          width: '2.6vh',
                          height: '2.6vh',
                          borderRadius: '50%',
                          border: '1px solid rgba(148,163,184,0.9)',
                          backgroundColor: !canEdit
                            ? 'rgba(148,163,184,0.45)'
                            : '#0f172a',
                          color: '#e5e7eb',
                          fontSize: '1.8vh',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: !canEdit ? 'not-allowed' : 'pointer',
                          padding: 0,
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}

                {/* Make it Flat toggle */}
                <div
                  style={{
                    marginTop: '1vh',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: flatDisabled ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      marginRight: '0.6vh',
                    }}
                  >
                    <input
                      type="checkbox"
                      id="flatCheckboxOverlay"
                      checked={Boolean(makeItFlat)}
                      onChange={(e) => {
                        if (!flatDisabled) setMakeItFlat?.(e.target.checked);
                      }}
                      disabled={flatDisabled}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        width: '2.4vh',
                        height: '2.4vh',
                        borderRadius: '0.5vh',
                        border: '0.25vh solid rgba(255,255,255,0.8)',
                        backgroundColor: flatDisabled
                          ? 'rgba(148,163,184,0.5)'
                          : makeItFlat
                          ? 'rgba(15,23,42,0.85)'
                          : 'rgba(15,23,42,0.6)',
                        display: 'inline-block',
                        cursor: flatDisabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                    {makeItFlat && (
                      <span
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -58%)',
                          fontSize: '1.6vh',
                          color: '#e5e7eb',
                          pointerEvents: 'none',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </div>

                  <label
                    htmlFor="flatCheckboxOverlay"
                    style={{
                      fontSize: '1.6vh',
                      userSelect: 'none',
                      cursor: flatDisabled ? 'default' : 'pointer',
                    }}
                  >
                    Make it Flat
                  </label>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Viewer / Fallback */}
      {hasGenerated ? (
        children
      ) : (
        <>
          {processedImage && (
            <img
              src={processedImage}
              alt="Processed"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                top: 0,
                left: 0,
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          )}
          {overlayImage && (
            <img
              src={overlayImage}
              alt="Overlay"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                top: 0,
                left: 0,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      )}
    </div>
  );
});

export default STLPreviewBox;
